"use client";

import Link from "next/link";
import { ArrowRight, Calendar, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n/I18nProvider";

interface BlogPost {
  id: string;
  slug: string;
  titleKey: string;
  excerptKey: string;
  dateKey: string;
  category: string;
  authorNameKey: string;
  authorRoleKey: string;
}

export default function BlogPage() {
  const { t } = useI18n();

  // Mock блог-посты (в будущем будут из API)
  const posts: BlogPost[] = [
    {
      id: "1",
      slug: "kak-zakryt-mesyats-otelya-usali",
      titleKey: "pages.blog.posts.monthclose.title",
      excerptKey: "pages.blog.posts.monthclose.excerpt",
      dateKey: "pages.blog.posts.monthclose.date",
      category: "USALI",
      authorNameKey: "pages.blog.posts.monthclose.authorName",
      authorRoleKey: "pages.blog.posts.monthclose.authorRole",
    },
    {
      id: "2",
      slug: "kpi-otelya-revpar-goppar",
      titleKey: "pages.blog.posts.kpi.title",
      excerptKey: "pages.blog.posts.kpi.excerpt",
      dateKey: "pages.blog.posts.kpi.date",
      category: "KPI",
      authorNameKey: "pages.blog.posts.kpi.authorName",
      authorRoleKey: "pages.blog.posts.kpi.authorRole",
    },
    {
      id: "3",
      slug: "approval-chain-otel",
      titleKey: "pages.blog.posts.approvalchain.title",
      excerptKey: "pages.blog.posts.approvalchain.excerpt",
      dateKey: "pages.blog.posts.approvalchain.date",
      category: "Процессы",
      authorNameKey: "pages.blog.posts.approvalchain.authorName",
      authorRoleKey: "pages.blog.posts.approvalchain.authorRole",
    },
    {
      id: "4",
      slug: "usali-101",
      titleKey: "pages.blog.posts.usali101.title",
      excerptKey: "pages.blog.posts.usali101.excerpt",
      dateKey: "pages.blog.posts.usali101.date",
      category: "USALI",
      authorNameKey: "pages.blog.posts.usali101.authorName",
      authorRoleKey: "pages.blog.posts.usali101.authorRole",
    },
  ];

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      USALI: "bg-keiro-mist text-keiro-green-dark",
      KPI: "bg-blue-50 text-blue-700",
      Процессы: "bg-purple-50 text-purple-700",
      Treasury: "bg-blue-50 text-blue-700",
      "Product Updates": "bg-amber-50 text-keiro-amber",
      Industry: "bg-purple-50 text-purple-700",
    };
    return colors[category] || "bg-slate-100 text-slate-700";
  };

  return (
    <div className="pt-28">
      {/* Hero Section */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-16">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
            {t("pages.blog.headline") || "Блог Keiront"}
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            {t("pages.blog.subheading") ||
              "Инсайты, тренды и best practices для финансового контроля в гостиничном бизнесе"}
          </p>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {posts.map((post) => (
            <Link
              key={post.id}
              href={`/blog/${post.slug}`}
              className="group border border-slate-200 rounded-xl overflow-hidden hover:border-keiro-green/50 transition-all duration-300 flex flex-col h-full hover:shadow-lg"
            >
              {/* Post Card Header */}
              <div className="p-6 flex-1 flex flex-col">
                {/* Category Badge */}
                <div className="mb-4">
                  <span
                    className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getCategoryColor(
                      post.category
                    )}`}
                  >
                    {post.category}
                  </span>
                </div>

                {/* Title */}
                <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-keiro-green transition-colors">
                  {t(post.titleKey) || post.titleKey}
                </h3>

                {/* Excerpt */}
                <p className="text-slate-600 leading-relaxed mb-6 flex-1">
                  {t(post.excerptKey) || post.excerptKey}
                </p>

                {/* Meta Info */}
                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <Calendar size={16} />
                    <span>{t(post.dateKey) || post.dateKey}</span>
                  </div>

                  {/* Author Info */}
                  <div className="pt-4 border-t border-slate-100">
                    <p className="text-sm font-medium text-slate-900">
                      {t(post.authorNameKey) || post.authorNameKey}
                    </p>
                    <p className="text-xs text-slate-600 mt-1">
                      {t(post.authorRoleKey) || post.authorRoleKey}
                    </p>
                  </div>
                </div>
              </div>

              {/* Post Card Footer - Read More */}
              <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 group-hover:bg-keiro-mist/30 transition-colors">
                <div className="flex items-center gap-2 text-keiro-green font-semibold">
                  <span>{t("pages.blog.readMore") || "Читать полностью"}</span>
                  <ArrowRight
                    size={18}
                    className="group-hover:translate-x-1 transition-transform"
                  />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Categories Section */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200">
        <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
          {t("pages.blog.categoriesTitle") || "Категории"}
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {["USALI", "KPI", "Процессы", "Treasury"].map(
            (cat) => (
              <div
                key={cat}
                className="text-center p-6 border border-slate-200 rounded-lg hover:border-keiro-green/50 transition-colors cursor-pointer"
              >
                <Tag size={24} className="text-keiro-green mx-auto mb-3" />
                <p className="font-semibold text-slate-900">{cat}</p>
              </div>
            )
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="bg-keiro-mist rounded-xl p-12 text-center border border-keiro-green/30">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">
            {t("pages.blog.newsletterTitle") ||
              "Получайте свежие инсайты каждую неделю"}
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            {t("pages.blog.newsletterDesc") ||
              "Подпишитесь на нашу еженедельную рассылку о финансовом контроле, USALI и гостиничных тренданах"}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input
              type="email"
              placeholder={t("pages.blog.emailPlaceholder") || "Ваш email"}
              className="flex-1 px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:border-keiro-green"
            />
            <Button variant="default" className="h-12 px-8">
              {t("pages.blog.subscribe") || "Подписаться"}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
